import { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import axios from "axios";
import toast from "react-hot-toast";
import moment from "moment";
import {
  ButtonComp,
  TextInputComp,
  FormikSelectComp,
  FormikDatePicker,
  ImageLoaderUpdate,
} from "../../../../components";
import { useGetBranchesQuery } from "../../../../store/admin/settings/branchSlice";
import { useUpdateStaffMutation } from "../../../../store/admin/staff";
import {
  useGetRolesQuery,
  useGetCountriesQuery,
} from "../../../../store/selectableSlice";
import { useUser } from "../../../../hooks";
import { isStaffSystemRole, staffCanManageRoles } from "../../../../utils/staffRoleModules";

const formatDateForInput = (dateString) => {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    return date.toISOString().split("T")[0];
  } catch {
    return "";
  }
};

const formatLastLogin = (value) => {
  if (!value) return "Never";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("DD-MMM-YYYY HH:mm") : "—";
};

const validationSchema = (canManageRoles) =>
  Yup.object({
    first_name: Yup.string().required("First name is required"),
    last_name: Yup.string().required("Last name is required"),
    email: Yup.string().email("Enter valid email").required("Required"),
    phone: Yup.string()
      .matches(/^[0-9]{11}$/, "Input 11 phone number")
      .required("Required"),
    dob: Yup.string().nullable(),
    role_id: canManageRoles
      ? Yup.string().required("Required")
      : Yup.string().nullable(),
    address: Yup.string().required("Address is required"),
    country_id: Yup.string().required("Required"),
    branch_id: Yup.string().required("Branch is required"),
  });

/**
 * Dedicated staff edit form (used on Edit Staff page).
 */
export default function StaffEditForm({
  staffId,
  staffDetails,
  onCancel,
  onSuccess,
}) {
  const { data: branches = [] } = useGetBranchesQuery();
  const { data: roles = [] } = useGetRolesQuery();
  const { data: countries = [] } = useGetCountriesQuery();
  const currentUser = useUser();
  const canManageRoles = staffCanManageRoles(currentUser?.role);
  const [updateStaff, { isLoading: isUpdatingStaff }] = useUpdateStaffMutation();

  const [imgFile, setImgFile] = useState(null);
  const [errorImg, setErrorImg] = useState(null);
  const [imgPreview, setImgPreview] = useState(null);
  const [profilePictureKey, setProfilePictureKey] = useState(0);

  const assignableRoles = (roles || []).filter((role) => {
    if (!role) return false;
    if (role.is_system || role.name === "Super Admin") {
      return isStaffSystemRole(currentUser?.role);
    }
    return true;
  });

  const currentRoleLabel =
    staffDetails?.role?.name ||
    assignableRoles.find((r) => String(r.id) === String(staffDetails?.role_id))
      ?.name ||
    "—";

  useEffect(() => {
    return () => {
      if (imgPreview) URL.revokeObjectURL(imgPreview);
    };
  }, [imgPreview]);

  const updateImgFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      setImgFile(null);
      setImgPreview(null);
      return setErrorImg("Upload an image");
    }
    const size = file.size / 1024 / 1024;
    const type = file.type?.split("/")[1];
    const validTypes = ["jpeg", "jpg", "png"];
    if (size > 10 || !validTypes.includes(type)) {
      setErrorImg("Invalid. File size must not be more than 10mb. Use jpg, jpeg or png.");
      setImgFile(null);
      setImgPreview(null);
      return;
    }
    setErrorImg(null);
    setImgFile(file);
    setImgPreview(URL.createObjectURL(file));
  };

  const clearPhotoSelection = () => {
    if (imgPreview) URL.revokeObjectURL(imgPreview);
    setImgFile(null);
    setImgPreview(null);
    setErrorImg(null);
  };

  const handleSubmit = async (formikValues, { setSubmitting }) => {
    if (errorImg) {
      setSubmitting(false);
      return;
    }

    const toastId = toast.loading("Updating staff...");
    try {
      let photoUrl = staffDetails?.photo;
      if (imgFile) {
        const cloudName =
          process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || "afrilocales";
        const uploadPreset =
          process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || "svmprhvf";
        const uploadUrl = `https://api.cloudinary.com/v1_1/${cloudName}/upload`;
        const photoData = new FormData();
        photoData.append("file", imgFile);
        photoData.append("upload_preset", uploadPreset);
        photoUrl = await axios
          .post(uploadUrl, photoData)
          .then((response) => response?.data?.secure_url)
          .catch(() => staffDetails?.photo);
      }

      const payload = {
        ...formikValues,
        photo: photoUrl,
        dob: formikValues.dob || null,
        branch_id:
          formikValues.branch_id ||
          staffDetails?.active_branch_id ||
          staffDetails?.branch_id,
      };

      if (!canManageRoles) {
        payload.role_id = staffDetails?.role_id;
      }

      const response = await updateStaff({ staffId, payload });

      if ("error" in response) {
        toast.error(response?.error?.data?.message || "An error occurred", {
          id: toastId,
        });
        return;
      }
      if (response?.data?.status === false) {
        toast.error(response?.data?.message || "An error occurred", {
          id: toastId,
        });
        return;
      }

      toast.success("Staff updated successfully", { id: toastId });
      clearPhotoSelection();
      setProfilePictureKey((k) => k + 1);
      onSuccess?.();
    } catch {
      toast.error("An error occurred while updating staff", { id: toastId });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={{
        first_name: staffDetails?.first_name || "",
        last_name: staffDetails?.last_name || "",
        email: staffDetails?.email || "",
        phone: staffDetails?.phone || "",
        dob: formatDateForInput(staffDetails?.dob),
        role_id: staffDetails?.role_id || "",
        address: staffDetails?.address || "",
        country_id: staffDetails?.country_id || "",
        branch_id:
          staffDetails?.active_branch_id || staffDetails?.branch_id || "",
      }}
      enableReinitialize
      validationSchema={validationSchema(canManageRoles)}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting }) => (
        <Form className="space-y-5">
          <div className="rounded-xl border border-[#e0e0e0] bg-[#fafafa] p-4">
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="relative shrink-0">
                <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-white shadow-sm ring-2 ring-[#e0e0e0]">
                  <ImageLoaderUpdate
                    key={`edit-${profilePictureKey}-${imgPreview || staffDetails?.photo}`}
                    src={imgPreview || staffDetails?.photo}
                    width="96"
                    height="96"
                    imgClass="rounded-full w-full h-full object-cover"
                  />
                </div>
                {imgFile ? (
                  <span className="absolute -bottom-1 -right-1 bg-[#19bc31] text-white text-[10px] font-semibold px-2 py-0.5 rounded-full">
                    New
                  </span>
                ) : null}
              </div>
              <div className="flex-1 text-center sm:text-left">
                <p className="fw-semibold text-[#383838] mb-1">Profile photo</p>
                <p className="text-sm text-[#525252] mb-3">
                  JPG or PNG, max 10MB
                </p>
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png"
                  onChange={updateImgFile}
                  className="hidden"
                  id="staff-edit-profile-picture"
                />
                <label
                  htmlFor="staff-edit-profile-picture"
                  className="inline-flex items-center px-4 py-2 bg-[var(--color-secondary)] text-white rounded-lg cursor-pointer hover:opacity-90 text-sm font-medium"
                >
                  {imgFile ? "Change selected photo" : "Upload photo"}
                </label>
                {errorImg ? (
                  <p className="text-danger small mt-2 mb-0">{errorImg}</p>
                ) : null}
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-[#e0e0e0] bg-white p-4">
            <p className="text-sm text-[#525252] mb-1">Last login</p>
            <p className="fw-semibold text-[#383838] mb-0">
              {formatLastLogin(staffDetails?.last_login_at)}
            </p>
          </div>

          <div className="rounded-xl border border-[#e0e0e0] bg-white p-4 md:p-5">
            <h4 className="text-base fw-semibold text-[#383838] mb-4">
              Personal information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextInputComp
                name="first_name"
                labelText="First name"
                placeholder="Enter first name"
                required
              />
              <TextInputComp
                name="last_name"
                labelText="Last name"
                placeholder="Enter last name"
                required
              />
              <div>
                <FormikDatePicker
                  fieldName="dob"
                  labelText="Date of birth"
                  placeholder="Select date of birth"
                />
                {!staffDetails?.dob ? (
                  <p className="text-sm text-[#525252] mt-1 mb-0">
                    No date of birth on file yet
                  </p>
                ) : null}
              </div>
              {canManageRoles ? (
                <FormikSelectComp
                  fieldName="role_id"
                  labelText="Role"
                  options={assignableRoles.map((role) => ({
                    value: role.id,
                    label: role.name,
                  }))}
                  selectText="Select role"
                />
              ) : (
                <div>
                  <p className="text-sm text-[#525252] mb-1">Role</p>
                  <p className="fw-semibold text-[#383838] mb-1">
                    {currentRoleLabel}
                  </p>
                  <p className="text-xs text-[#525252] mb-0">
                    Role Management permission is required to change roles.
                  </p>
                </div>
              )}
              <FormikSelectComp
                fieldName="branch_id"
                labelText="Active branch"
                options={(branches || []).map((branch) => ({
                  value: branch.id,
                  label: branch.name,
                }))}
                selectText="Select branch"
              />
            </div>
          </div>

          <div className="rounded-xl border border-[#e0e0e0] bg-white p-4 md:p-5">
            <h4 className="text-base fw-semibold text-[#383838] mb-4">
              Contact information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextInputComp
                name="email"
                labelText="Email address"
                placeholder="Enter email address"
                type="email"
                required
              />
              <TextInputComp
                name="phone"
                labelText="Phone number"
                placeholder="Enter 11-digit phone number"
                required
              />
              <div className="md:col-span-2">
                <TextInputComp
                  name="address"
                  labelText="Address"
                  placeholder="Enter full address"
                  required
                />
              </div>
              <FormikSelectComp
                fieldName="country_id"
                labelText="Country"
                options={(countries || []).map((country) => ({
                  value: country.id,
                  label: country.name,
                }))}
                selectText="Select country"
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-end pt-2">
            <ButtonComp
              type="button"
              btnText="Cancel"
              BorderColor="#a1a0a0"
              btnClassName="px-6 py-2 rounded-lg text-[#383838] font-medium"
              ButtonInlineStyle={{
                backgroundColor: "#ffffff",
                minWidth: "140px",
              }}
              onClick={onCancel}
            />
            <ButtonComp
              type="submit"
              btnText={
                isSubmitting || isUpdatingStaff ? "Saving..." : "Save changes"
              }
              loading={isSubmitting || isUpdatingStaff}
              disabled={isSubmitting || isUpdatingStaff}
              btnClassName="px-6 py-2 rounded-lg text-white font-medium"
              ButtonInlineStyle={{
                backgroundColor: "var(--color-primary)",
                border: "none",
                minWidth: "160px",
              }}
            />
          </div>
        </Form>
      )}
    </Formik>
  );
}
